import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { Dietitian } from '../../dietitian/domain/dietitian';
import { Patient } from '../../patient/domain/patient';
import {
  TranscriptionRepository,
  TranscriptionSchema,
} from '../repositories/transcription_repository';
import { TranscriptionEventProvider } from './events/transcription_event_provider';
import {
  TranscriptionChanged,
  TranscriptionCreated,
  TranscriptionDeleted,
  TranscriptionUpdated,
} from './events/transcription_events';
import { Transcription } from './transcription';
import { TranscriptionUpload } from './transcription_upload';

@Injectable()
export class TranscriptionCommands {
  private transcriptionEventSubscription: Subscription;
  constructor(
    private repository: TranscriptionRepository,
    private eventProvider: TranscriptionEventProvider,
    private toastr: ToastrService,
  ) {
    this.transcriptionEventSubscription?.unsubscribe();
    this.transcriptionEventSubscription = this.eventProvider.events$.subscribe(
      (event) => {
        if (event instanceof TranscriptionCreated) {
          this.toastr.success('Transcription lancée (Env. 5 minutes)');
        }
        if (event instanceof TranscriptionDeleted) {
          this.toastr.success('Transcription supprimée');
        }
        if (event instanceof TranscriptionUpdated) {
          this.toastr.success('Transcription modifiée');
        }
        if (event instanceof TranscriptionChanged) {
          if (event.entity.status !== 'initial') {
            const transcription = event.entity;
            if (transcription.status == 'uploaded') {
              // this.toastr.success('Fichier audio téléchargé');
            }

            if (transcription.status == 'queued') {
              this.toastr.success('Fichier audio en attente de traitement');
            }
            if (transcription.status == 'downloading') {
              // this.toastr.success('Lecture du fichier audio en cours');
            }
            if (transcription.status == 'transcribing') {
              this.toastr.success('Transcription audio vers texte en cours');
            }
            if (transcription.status == 'aligning') {
              // this.toastr.success('Alignement du texte transcrit en cours');
            }
            if (transcription.status == 'diarizing') {
              this.toastr.success('Étiquetage du texte transcrit en cours');
            }
            if (transcription.status == 'anonymizing') {
              this.toastr.success('Anonymisation du texte transcrit en cours');
            }

            if (transcription.status == 'done') {
              this.toastr.success('Transcription audio terminée');
            }
            if (transcription.status == 'error') {
              this.toastr.error('Transcription audio a échouée');
            }
          }
        }
      },
    );
  }

  instanceTranscriptionWithBlob(
    dietitian: Dietitian,
    patient: Patient,
    blob: Blob,
    deleteAudio: boolean,
  ) {
    const transcriptionId = new UniqueEntityID();
    return Transcription.create(
      {
        dietitianId: dietitian.dietitianId,
        patientId: patient.patientId,
        blob: blob,
        name: transcriptionId.toString(),
        label: 'Transcription du ' + new Date().toLocaleDateString(),
        size: blob.size,
        mimeType: blob.type,
        status: 'initial',
        path: Transcription.getDietitianPatientTranscriptionPath(
          dietitian.id.toString(),
          patient.id.toString(),
          transcriptionId.toString(),
          deleteAudio,
        ),
        reports: undefined,
      },
      transcriptionId,
    );
  }

  async getTranscription(transcriptionId: string): Promise<Transcription> {
    return this.repository.load(transcriptionId);
  }

  upload(transcription: Transcription): TranscriptionUpload {
    return this.repository.upload(transcription);
  }

  async listen(transcriptionId: string) {
    const sub = this.repository
      .valueChanges(transcriptionId)
      .subscribe((schema) => {
        if (schema) {
          const transcription = this.repository.fromSchema(
            schema,
            transcriptionId,
          );
          this.eventProvider.dispatch(new TranscriptionChanged(transcription));
          if (
            transcription.status == 'done' ||
            transcription.status == 'error'
          ) {
            sub.unsubscribe();
          }
        }
      });
    return sub;
  }

  async create(transcription: Transcription): Promise<UniqueEntityID> {
    transcription = await this.repository.create(
      transcription,
      transcription.id.toString(),
    );
    this.eventProvider.dispatch(new TranscriptionCreated(transcription));
    return transcription.id;
  }

  async update(transcription: Transcription): Promise<Transcription> {
    transcription = await this.repository.save(transcription);
    this.eventProvider.dispatch(new TranscriptionUpdated(transcription));
    return transcription;
  }

  valueChanges(
    transcriptionId: string | undefined,
  ): Observable<TranscriptionSchema | undefined> {
    return this.repository.valueChanges(transcriptionId);
  }

  async findAll(dietId: string, patientId: string) {
    return this.repository.findAll(dietId, patientId);
  }

  async delete(transcriptionId: string) {
    const transcription = await this.getTranscription(transcriptionId);
    await this.repository.delete(transcriptionId);
    this.eventProvider.dispatch(new TranscriptionDeleted(transcription));
  }

  async rename(transcriptionId: string, label: string) {
    const current = await this.repository.load(transcriptionId);
    current.label = label;
    await this.repository.update(current);
    this.eventProvider.dispatch(new TranscriptionUpdated(current));
  }
}
